import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';

const PresetCaptureSetting = ({ preset }) => {
    const [isLoading, setIsLoading] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetCapturePage, setPresetCapturePage] = useState(null);

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        timerSeconds: 10,
        shootingCount: 8,
        aiModeSize: 50,
        aiModeColor: '#FFFFFF',
        isActive: true
    });

    // 페이지 데이터 로드
    useEffect(() => {
        loadCapturePageData();
    }, [presetId]);

    // 촬영 페이지 데이터 로드
    const loadCapturePageData = async () => {
        try {
            setIsLoading(true);
            const capturePage = await presetApi.getCapturePage(presetId);
            
            setPresetCapturePage(capturePage);
            
            setFormData({
                timerSeconds: capturePage.timerSeconds ?? 10,
                shootingCount: capturePage.shootingCount ?? 8,
                aiModeSize: capturePage.aiModeSize ?? 50,
                aiModeColor: capturePage.aiModeColor ?? '#FFFFFF',
                isActive: capturePage.isActive ?? true
            });
        } catch (error) {
            console.error('촬영 페이지 데이터 로드 실패:', error);
            // 에러 시 기본값 유지
        } finally {
            setIsLoading(false);
        }
    };

    // 타이머 조절 핸들러
    const handleTimerChange = (action) => {
        let newTimer;
        if (action === 'increase') {
            newTimer = Math.min(formData.timerSeconds + 1, 60);
        } else {
            newTimer = Math.max(formData.timerSeconds - 1, 1);
        }
        
        setFormData({
            ...formData,
            timerSeconds: newTimer
        });
    };

    // 촬영 횟수 조절 핸들러
    const handleShootingCountChange = (action) => {
        let newCount;
        if (action === 'increase') {
            newCount = Math.min(formData.shootingCount + 1, 20);
        } else {
            newCount = Math.max(formData.shootingCount - 1, 1);
        }
        
        setFormData({
            ...formData,
            shootingCount: newCount
        });
    };

    // AI 모드 크기 변경 핸들러
    const handleAIModeSize = (value) => {
        const size = parseInt(value) || 50;
        setFormData({
            ...formData,
            aiModeSize: size
        });
    };

    // AI 모드 색상 변경 핸들러
    const handleAIModeColor = (color) => {
        setFormData({
            ...formData,
            aiModeColor: color
        });
    };

    // 저장 이벤트 리스너
    useEffect(() => {
        const eventName = `savePagePreset_shooting_mode`;

        const saveHandler = async (event) => {
            if (!preset?.id) {
                alert("프리셋 정보가 없습니다.");
                return;
            }

            try {
                setIsLoading(true);

                const dataToSave = {
                    presetId: event.detail.presetId,
                    timerSeconds: formData.timerSeconds,
                    shootingCount: formData.shootingCount,
                    aiModeSize: formData.aiModeSize,
                    aiModeColor: formData.aiModeColor,
                    isActive: formData.isActive
                };
                
                const response = await presetApi.saveCapturePage(event.detail.presetId, dataToSave);
                console.log("촬영 페이지 저장 성공:", response);

            } catch (error) {
                console.error("촬영 페이지 저장 실패:", error);
                alert("촬영 페이지 저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        document.addEventListener(eventName, saveHandler);

        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [preset?.id, formData]);

    if (isLoading) {
        return <div className="preset-settings">로딩 중...</div>;
    }

    return (
        <div className="preset-settings">
            {/* 사진촬영 옵션 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="timer-input" 
                            value={formData.timerSeconds}
                            readOnly
                        />
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">사진 촬영 횟수 *최대 20회</div>
                    <div className="shooting-count-container">
                        <button 
                            className="shooting-count-btn decrease-btn"
                            onClick={() => handleShootingCountChange('decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="shooting-count-input" 
                            value={formData.shootingCount}
                            readOnly
                        />
                        <button 
                            className="shooting-count-btn increase-btn"
                            onClick={() => handleShootingCountChange('increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="unit-text">회</span>
                    </div>
                </div>
            </div>
            
            {/* AI 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">AI 모드 표시</h4>
                <div className="settings-row">
                    <div className="settings-label">사이즈 조절</div>
                    <div className="size-slider-container">
                        <i className="ri-subtract-line size-icon small"></i>
                        <input 
                            type="range" 
                            className="size-slider" 
                            min="10" 
                            max="100" 
                            value={formData.aiModeSize}
                            onChange={(e) => handleAIModeSize(e.target.value)}
                        />
                        <i className="ri-add-line size-icon large"></i>
                        <span className="size-value">{formData.aiModeSize}%</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">색상</div>
                    <div className="color-picker-container">
                        <input 
                            type="text" 
                            className="color-input" 
                            value={formData.aiModeColor} 
                            readOnly 
                        />
                        <input 
                            type="color" 
                            className="color-picker" 
                            value={formData.aiModeColor}
                            onChange={(e) => handleAIModeColor(e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PresetCaptureSetting;
